import React, { useState, useMemo, type ReactNode } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import SourceLink from './SourceLink'

import { MOVEMENT_TYPES, MOVEMENT_TYPE_COLORS, useMovementTypeLabel } from '@constants/inventoryMovement'

interface MovementTypeTagProps {
  type: string;
}

const MovementTypeTag: React.FC<MovementTypeTagProps> = ({ type }) => {
  const movementTypeLabel = useMovementTypeLabel()

  return (
    <Tag color={MOVEMENT_TYPE_COLORS[type] || 'default'}>
      {movementTypeLabel(type)}
    </Tag>
  )
}

interface QuantityCellProps {
  quantity: number;
  quantity_after: number;
}

const QuantityCell: React.FC<QuantityCellProps> = ({ quantity, quantity_after }) => {
  const diff = quantity_after - (quantity_after - quantity)
  const isPositive = diff > 0
  const isNegative = diff < 0

  return (
    <Space direction="vertical" size={0}>
      <div style={{
        fontFamily: 'monospace',
        fontSize: '14px',
        color: isPositive ? '#52c41a' : isNegative ? '#ff4d4f' : '#000'
      }}>
        {isPositive && '+'}
        {quantity}
      </div>
      <div style={{ fontSize: '12px', color: '#999' }}>
        → {quantity_after}
      </div>
    </Space>
  )
}

interface MerchantOption {
  id: string;
  name: string;
}

interface InventoryMovementsService extends TableService {
  export: (ids: unknown[], language: string) => Promise<unknown>;
}

interface InventoryMovementsListProps {
  role?: 'admin' | 'operator';
  service: InventoryMovementsService;
  merchants?: MerchantOption[];
  addJob?: (...args: any[]) => void;
  hideMerchantUI?: boolean;
}

interface InventoryMovementRow {
  id: string;
  product_id?: string;
  product_name?: string;
  product_sku?: string;
  merchant_id?: string;
  movement_type?: string;
  quantity?: number;
  quantity_after?: number;
  batch?: string;
  expiration_date?: string;
  source_type?: string;
  source_parent_id?: string;
  note?: string;
  created_at?: string;
  [key: string]: unknown;
}

const InventoryMovementsList: React.FC<InventoryMovementsListProps> = ({
  role = 'admin',
  service, // New
  merchants = [],
  addJob,
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t, i18n } = useTranslation()
  const movementTypeLabel = useMovementTypeLabel()
  const [lastModified, setLastModified] = useState(0)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const columns = useMemo(() => {
    const cols: unknown[] = [
      {
        nameKey: `models.inventory_movement.product_name`,
        persist: true,
        sortable: false,
        width: '200px',
        wrap: true,
        cell: ({ product_id, product_name }: InventoryMovementRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/products/${product_id}`} style={{ wordBreak: 'break-word' }}>
              {product_name}
            </Link>
            <a
              href={`${urlPrefix}/products/${product_id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      }
    ];

    // Add merchant column for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: `models.inventory_movement.merchant`,
        sortable: false,
        wrap: true,
        selector: ({ merchant_id }: InventoryMovementRow) => {
          return merchants.find((merchant) => merchant.id === merchant_id)?.name
        },
      });
    }

    cols.push(
      {
        nameKey: `models.inventory_movement.sku`,
        sortable: false,
        wrap: true,
        selector: ({ product_sku }: InventoryMovementRow) => product_sku,
      },
      {
        name: (
          <Space size={4}>
            {t('models.inventory_movement.movement_type')}
            <Tooltip title={t('shared.inventory_movements.list.column_tooltips.movement_type')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: '120px',
        cell: ({ movement_type }: InventoryMovementRow) => (
          <MovementTypeTag type={movement_type as string} />
        ),
      },
      {
        name: (
          <Space size={4}>
            {t('models.inventory_movement.quantity')}
            <Tooltip title={t('shared.inventory_movements.list.column_tooltips.quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: '100px',
        cell: ({ quantity, quantity_after }: InventoryMovementRow) => (
          <QuantityCell quantity={quantity as number} quantity_after={quantity_after as number} />
        ),
      },
      {
        nameKey: `models.inventory_movement.batch`,
        sortable: false,
        wrap: true,
        selector: ({ batch }: InventoryMovementRow) => batch || '-',
      },
      {
        nameKey: `models.inventory_movement.expiration_date`,
        sortable: true,
        sortFieldName: 'inventories.expiration_date',
        width: '120px',
        selector: ({ expiration_date }: InventoryMovementRow) => expiration_date || '-',
      },
      {
        name: (
          <Space size={4}>
            {t('models.inventory_movement.source')}
            <Tooltip title={t('shared.inventory_movements.list.column_tooltips.source')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: '150px',
        wrap: true,
        cell: ({ source_type, source_parent_id }: InventoryMovementRow) => (
          <SourceLink
            sourceType={source_type}
            sourceParentId={source_parent_id}
            role={role}
          />
        ),
      },
      {
        nameKey: `models.inventory_movement.note`,
        wrap: true,
        width: '150px',
        selector: ({ note }: InventoryMovementRow) => note || '-',
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'inventory_movements.created_at',
        width: '180px',
        selector: ({ created_at }: InventoryMovementRow) => created_at,
      }
    );

    return cols;
  }, [role, urlPrefix, merchants, t, hideMerchantUI])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = [
      {
        label: t(`models.inventory_movement.sku`),
        component: MultipleValue,
        inputName: 'products.sku',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.inventory_movements.list.filter_tooltips.sku.title'),
          content: t('shared.inventory_movements.list.filter_tooltips.sku.content'),
        },
      },
      {
        label: t(`models.inventory_movement.product_name`),
        component: MultipleValue,
        inputName: 'products.name',
        createable: true,
        tooltip: {
          title: t('shared.inventory_movements.list.filter_tooltips.product_name.title'),
          content: t('shared.inventory_movements.list.filter_tooltips.product_name.content'),
        },
      }
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`models.inventory_movement.merchant`),
        component: MultipleValue,
        inputName: 'merchants.id',
        createable: false,
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.inventory_movements.list.filter_tooltips.merchant.title'),
          content: t('shared.inventory_movements.list.filter_tooltips.merchant.content'),
        },
      });
    }

    rules.push(
      {
        label: t(`models.inventory_movement.movement_type`),
        component: MultipleValue,
        inputName: 'inventory_movements.movement_type',
        defaultPinned: true,
        createable: false,
        defaultOptions: MOVEMENT_TYPES.map((type) => ({
          label: movementTypeLabel(type),
          value: type,
        })),
        tooltip: {
          title: t('shared.inventory_movements.list.filter_tooltips.movement_type.title'),
          content: t('shared.inventory_movements.list.filter_tooltips.movement_type.content'),
        },
      },
      {
        label: t(`models.inventory_movement.batch`),
        component: MultipleValue,
        inputName: 'inventories.batch',
        createable: true,
        tooltip: {
          title: t('shared.inventory_movements.list.filter_tooltips.batch.title'),
          content: t('shared.inventory_movements.list.filter_tooltips.batch.content'),
        },
      },
      {
        label: t(`models.inventory_movement.expiration_date`),
        component: FullDayRangePicker,
        inputName: 'inventories.expiration_date',
        tooltip: {
          title: t('shared.inventory_movements.list.filter_tooltips.expiration_date.title'),
          content: t('shared.inventory_movements.list.filter_tooltips.expiration_date.content'),
        },
      },
      {
        label: t(`common.created_at`),
        component: FullDayRangePicker,
        inputName: 'inventory_movements.created_at',
        defaultPinned: true,
        tooltip: {
          title: t('shared.inventory_movements.list.filter_tooltips.created_at.title'),
          content: t('shared.inventory_movements.list.filter_tooltips.created_at.content'),
        },
      }
    );

    return rules;
  }, [role, t, merchants, hideMerchantUI, movementTypeLabel])

  const actions = useMemo<TableAction[]>(() => [
    {
      name: 'common.button.export',
      className: 'btn btn-primary btn-outline mb-0',
      do: (selected: unknown) => executeBatchOperation({
        task: () => service.export(Array.from(selected as Iterable<unknown>), i18n.language),
        jobName: 'common.button.export'
      })
    } as TableAction
  ], [executeBatchOperation, service, i18n])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'prefix',
        searchFields: [
          t('models.inventory_movement.sku'),
          t('models.inventory_movement.product_name'),
          t('models.inventory_movement.batch'),
        ],
      }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}InventoryHistoriesTable`}
      defaultQuery={{
        sorting_field: 'inventory_movements.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      actions={actions}
      service={service}
      filterHelpExample={t('shared.inventory_movements.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/inventory_movements/list#features`}
    />
  )
}

export default InventoryMovementsList
